import React, { useContext, useState } from 'react'
import Dropdown from 'react-bootstrap/Dropdown'
import OverlayTrigger from 'react-bootstrap/OverlayTrigger'
import Tooltip from 'react-bootstrap/Tooltip'
import { useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faClone,
  faDownload,
  faEllipsisV,
  faEnvelope,
  faPen,
  faPlus,
  faUserPlus
} from '@fortawesome/free-solid-svg-icons'
import UserContext from 'context/user-context'
import AuthContext from 'context/auth-context'
import ConfirmDelete from 'shared/confirm-delete'
import ShareButton from 'shared/buttons/share-button'
import {
  useDashboard,
  useDestroyDashboard,
  useDuplicateDashboard
} from 'api/hooks/use-dashboards'
import { useCreateDashboardUser } from 'api/hooks/use-dashboard-user'
import { useCreateDashboardTemplate } from 'api/hooks/use-dashboard-templates'
import { baseUrl } from 'api/helpers'
import { triggerDashboardPdfExport } from 'services/dashboardPdfService'
import NewView from 'dashboard/new'
import DashboardUserRow from 'dashboard/user-row'
import ScheduleEmailButton from 'dashboard/schedule-email-button'
import CreateTemplateModal from 'dashboard/create-template-modal'

// Samples attached to a view outlive it (audit_reports are nullified, not destroyed), so say so
// rather than let the user think their sample work goes with it.
const deleteMessage = (dashboard) => {
  const sampleCount = dashboard.audit_reports_count || 0
  const samplesNote = sampleCount > 0
    ? ` Its ${sampleCount} sample${sampleCount === 1 ? '' : 's'} will be kept but no longer attached to a view.`
    : ''
  return `Delete "${dashboard.name}"?${samplesNote} This cannot be undone.`
}

// The index row carries no filters, columns or account search, and a template is built from all of
// them — so the full view is fetched, and only once the admin has asked for the modal.
function CreateTemplateFromView({ dashboardId, onHide }) {
  const { data: dashboard } = useDashboard({ id: dashboardId, options: {} })
  const { mutate: createTemplate, isLoading } = useCreateDashboardTemplate({
    options: {
      onSuccess: (data) => {
        onHide()
        toast.success(`Template "${data.name}" created successfully!`)
      },
      onError: (error) => toast.error(`Failed to create template: ${error.message}`)
    }
  })

  return (
    <CreateTemplateModal
      show={!!dashboard}
      onHide={onHide}
      dashboard={dashboard}
      isLoading={isLoading}
      onSubmit={(templateData) =>
        createTemplate({ sectionId: dashboard.section_id, dashboardId, templateData })
      }
    />
  )
}

// Per-view actions kebab for the Views index — the actions dashboard/show.jsx offers for a single
// view, gated the same way, in the shape of report-builder's ReportActionsMenu. Each modal is
// mounted only while open: most of them fetch on mount, and the index renders one menu per card.
// `children` render at the top of the menu, for actions that belong to the index (Remove from folder).
function ViewActionsMenu({ dashboard, align = 'end', children }) {
  const user = useContext(UserContext)
  const token = useContext(AuthContext)
  const queryClient = useQueryClient()
  const [showEdit, setShowEdit] = useState(false)
  const [showShare, setShowShare] = useState(false)
  const [showSchedule, setShowSchedule] = useState(false)
  const [showTemplate, setShowTemplate] = useState(false)
  const [showDelete, setShowDelete] = useState(false)
  const [isExporting, setIsExporting] = useState(false)

  // Mirrors show.jsx: the owner edits, shares and deletes; editors (shared directly or through the
  // folder) can schedule emails; anyone who can see the view can duplicate and export it.
  const isOwner = dashboard.user_id === user.id
  const isEditor =
    (dashboard.shared_users?.find((su) => su.user_id === user.id)?.is_editor ?? false) ||
    !!dashboard.is_folder_editor
  const canSchedule = isOwner || isEditor

  const { mutate: duplicateDashboard, isLoading: isDuplicating } = useDuplicateDashboard({
    id: dashboard.id,
    options: {
      // duplications#create answers with the new id only; the list refetch brings the copy in.
      onSuccess: () => toast.success(`Duplicated "${dashboard.name}"`),
      onError: () => toast.error('Could not duplicate the view')
    }
  })

  const { mutate: destroyDashboard } = useDestroyDashboard({
    id: dashboard.id,
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries('dashboards')
        queryClient.invalidateQueries('folders')
        // Its samples stay, detached — their cards say which view they belong to.
        queryClient.invalidateQueries('reports')
        // The user's default view may have been the one just deleted.
        queryClient.invalidateQueries('current-user')
        toast.success('View deleted')
      },
      onError: () => toast.error('Failed to delete view')
    }
  })

  const handleExportPdf = async () => {
    setIsExporting(true)
    try {
      await triggerDashboardPdfExport({ dashboardId: dashboard.id, token, baseUrl })
      toast.success('PDF export queued. The file will be available in Exports shortly.')
    } catch {
      toast.error('PDF export failed. Please try again.')
    } finally {
      setIsExporting(false)
    }
  }

  return (
    <>
      {/* stopPropagation on pointerdown too, so the dnd-kit drag activator on the card or row doesn't swallow the toggle. */}
      <Dropdown onClick={(e) => e.stopPropagation()} onPointerDown={(e) => e.stopPropagation()}>
        <Dropdown.Toggle
          variant="link"
          bsPrefix="btn"
          className="text-muted p-1 border-0 rounded"
          style={{ lineHeight: 1 }}
          aria-label="View actions"
          data-testid={`view-actions-${dashboard.id}`}
        >
          <FontAwesomeIcon icon={faEllipsisV} />
        </Dropdown.Toggle>
        <Dropdown.Menu align={align}>
          {children}
          {isOwner && (
            <Dropdown.Item onClick={() => setShowShare(true)}>
              <FontAwesomeIcon icon={faUserPlus} fixedWidth className="me-2 text-muted" />
              Share…
            </Dropdown.Item>
          )}
          {canSchedule && (
            <Dropdown.Item onClick={() => setShowSchedule(true)}>
              <FontAwesomeIcon icon={faEnvelope} fixedWidth className="me-2 text-muted" />
              Schedule email
            </Dropdown.Item>
          )}
          <Dropdown.Item onClick={handleExportPdf} disabled={isExporting}>
            <FontAwesomeIcon icon={faDownload} fixedWidth className="me-2 text-muted" />
            Export PDF
          </Dropdown.Item>
          <Dropdown.Divider />
          {isOwner && (
            <Dropdown.Item onClick={() => setShowEdit(true)}>
              <FontAwesomeIcon icon={faPen} fixedWidth className="me-2 text-muted" />
              Edit details…
            </Dropdown.Item>
          )}
          <Dropdown.Item onClick={() => duplicateDashboard()} disabled={isDuplicating}>
            <FontAwesomeIcon icon={faClone} fixedWidth className="me-2 text-muted" />
            Duplicate
          </Dropdown.Item>
          {user.is_admin && (
            <Dropdown.Item onClick={() => setShowTemplate(true)}>
              <FontAwesomeIcon icon={faPlus} fixedWidth className="me-2 text-muted" />
              Create template…
            </Dropdown.Item>
          )}
          {isOwner && (
            <>
              <Dropdown.Divider />
              <Dropdown.Item className="text-danger" onClick={() => setShowDelete(true)}>Delete</Dropdown.Item>
            </>
          )}
          {/* Editors see Delete so the option isn't a mystery, but only the owner can delete. A
              disabled item swallows pointer events, so the tooltip hangs off a wrapper around it. */}
          {!isOwner && isEditor && (
            <>
              <Dropdown.Divider />
              <OverlayTrigger
                placement="left"
                overlay={<Tooltip id={`view-delete-disabled-${dashboard.id}`}>You cannot delete a view that you do not own</Tooltip>}
              >
                <div data-testid="view-delete-disabled">
                  <Dropdown.Item disabled aria-disabled="true">Delete</Dropdown.Item>
                </div>
              </OverlayTrigger>
            </>
          )}
        </Dropdown.Menu>
      </Dropdown>

      {/* Modals portal to <body>, but React still bubbles their events through this tree — a click
          up to the card (which opens the view), a pointerdown up to the drag wrapper. */}
      <span
        style={{ display: 'contents' }}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
      >
        {showEdit && (
          <NewView dashboard={dashboard} show onHide={() => setShowEdit(false)} />
        )}

        {showShare && (
          <ShareButton
            resource={dashboard}
            resourceName={dashboard.name}
            useCreateMutation={useCreateDashboardUser}
            createParams={{ dashboard_id: dashboard.id }}
            queryKeys={[`dashboards/${dashboard.id}`, 'dashboards']}
            UserRowComponent={DashboardUserRow}
            userRowProps={(u) => ({ dashboardUser: u, editable: isOwner, dashboardId: dashboard.id })}
            show
            onHide={() => setShowShare(false)}
          />
        )}

        {showSchedule && (
          <ScheduleEmailButton dashboard={dashboard} hideTrigger show setShow={setShowSchedule} />
        )}

        {showTemplate && (
          <CreateTemplateFromView dashboardId={dashboard.id} onHide={() => setShowTemplate(false)} />
        )}

        <ConfirmDelete
          id={dashboard.id}
          handleDelete={() => destroyDashboard()}
          show={showDelete}
          setShow={setShowDelete}
          hideButton
          message={deleteMessage(dashboard)}
          confirmLabel="Yes, delete"
          cancelLabel="Cancel"
        />
      </span>
    </>
  )
}

export default ViewActionsMenu
